import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import {
    Footer, Header, IndexedObject, Search, ComposableHeaderDropdownProviderProps, LayoutProps, isBrowser
} from "@themuse/design-system";
import { UseComboboxStateChange } from "downshift"; // eslint-disable-line import/no-extraneous-dependencies

import { useAppState } from "hooks/AppContext";
import { fetchCompanySuggestions, CompanySuggestion } from "utils/api";
import { contentTabIds } from "utils/constants";
import { getPathParts } from "utils/url";
import { pushDataLayerEvent } from "utils/tracking";
import { Hero } from "./Hero";
import { TabMain } from "./TabMain";

const CompanyNav = dynamic(
    () => import("./CompanyNav").then((x) => x.CompanyNav),
    { ssr: false }
);

const TabArticles = dynamic(
    () => import("./TabArticles").then((x) => x.TabArticles),
    { ssr: false }
);

const TabTeam = dynamic(
    () => import("./TabTeam").then((x) => x.TabTeam),
    { ssr: false }
);

const updateInput = async (
    changes: UseComboboxStateChange<IndexedObject>,
    setItems: (items: IndexedObject[]) => void,
    setInputValue: (inputValue: string) => void,
    fetchData:(inputValue: string) => Promise<CompanySuggestion[]>
) => {
    const { inputValue } = changes;
    const jobSearchItem = {
        label: `${inputValue}`,
        value: `/search/keyword/${inputValue}/`,
    };
    setInputValue(inputValue);
    if (inputValue) {
        setItems([jobSearchItem]);
        const companyResults = await fetchData(inputValue);
        const companyList = companyResults?.length ? formatCompanySuggestions(companyResults) : [];
        setItems([jobSearchItem].concat(companyList));
    } else {
        setItems([]);
    }
};

const formatCompanySuggestions = (
    companySuggestions: CompanySuggestion[]
) => companySuggestions.slice(0, 4).map((companyResult: CompanySuggestion) => {
    const { text, value } = companyResult;
    const { short_name } = value; // eslint-disable-line camelcase
    return {
        label: `${text}`,
        value: `/profiles/${short_name}` // eslint-disable-line camelcase
    };
});

const CompanyPage = (props: LayoutProps & React.PropsWithChildren<React.ComponentProps<typeof Header>>) => {
    const {
        pageId = "main-content",
        headerClassName,
        footerClassName,
    } = props;
    const { user } = useAppState();
    const router = useRouter();
    const [dropdownItems, setDropdownItems] = useState([] as IndexedObject[]);
    const [inputValue, setInputValue] = useState("");
    const [tabKey, setTabKey] = useState<string>("main");

    const searchQuerySubmittedEvent = (selectedItemText: string) => {
        const searchTerm = selectedItemText || inputValue;
        const eventProps = {
            search_term: searchTerm || ""
        };
        pushDataLayerEvent("searchQuerySubmitted", eventProps);
    };

    const onKeyboardSelectItem = (_: React.KeyboardEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        if (!isBrowser()) return "";

        const searchPath = inputValue
            ? `/search/keyword/${inputValue}`
            : "/search";
        const selectPath = selectedItem?.value
            ? selectedItem?.value
            : searchPath;

        searchQuerySubmittedEvent(selectedItem?.label);

        return window.open(selectPath, "_blank").focus();
    };

    const onMouseSelectItem = (_: React.MouseEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        searchQuerySubmittedEvent(selectedItem.label);
    };

    const dropdownProps: ComposableHeaderDropdownProviderProps = {
        IconComponent: <Search />,
        debounceWait: 10,
        dropdownList: dropdownItems,
        inputId: "search-input",
        onInputChange: (changes: UseComboboxStateChange<IndexedObject>) => updateInput(
            changes,
            setDropdownItems,
            setInputValue,
            fetchCompanySuggestions
        ),
        noResultsMessage: " ",
        onKeyboardSelectItem,
        onMouseSelectItem
    };

    const headerWithUserProps = {
        ...props,
        user,
    };

    useEffect(() => {
        const { query } = getPathParts(router.asPath);
        const locationTab = new URLSearchParams(query).get("tab");
        const nextTabKey = contentTabIds[locationTab] ? locationTab : "main";
        setTabKey(nextTabKey);
    }, [router.asPath]);

    return (
        <>
            <Header
                mainContainerId={pageId}
                className={headerClassName}
                enableCompanySearch={true}
                companySearchDropdownProps={dropdownProps}
                activePath="/companies"
                showSubMenus={true}
                {...headerWithUserProps}
            />
            <Hero />
            <CompanyNav />
            <main id={pageId} className="pageContent">
                {(tabKey === "main") && <TabMain />}
                {(tabKey === "articles") && <TabArticles />}
                {(tabKey === "team") && <TabTeam />}
            </main>

            <Footer className={footerClassName} />
        </>
    );
};

CompanyPage.displayName = "CompanyPage";

export { CompanyPage };
